import React, { useState } from "react";
import IntlMessages from "@iso/components/utility/intlMessages";
import { Button, Tag, Modal, Input, message } from "antd";
import moment from "moment";
import StopDeviceAlarmRequestModalWrapper from "./StopDeviceAlarmRequestModal.styles";
import Table from "@iso/components/collapsing-table/table";
import { alertDeviceService } from "@iso/services";

const { TextArea } = Input;

const STATUS_COLORS = {
  terminate: "red",
  responding: "gold",
  "not-available": "orange",
};

const STATUS_LABELS = {
  terminate: "Terminated",
  responding: "Responding",
  "not-available": "Not Available",
};

const StopDeviceAlarmRequestModal = (props) => {
  const { alarmRequests, visible, cancelModal, currentUserId, onStatusUpdated } = props;
  const [localRequests, setLocalRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [terminateModalVisible, setTerminateModalVisible] = useState(false);
  const [terminateRecord, setTerminateRecord] = useState(null);
  const [terminateReason, setTerminateReason] = useState("");
  const [terminateComment, setTerminateComment] = useState("");
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [editReason, setEditReason] = useState("");
  const [editComment, setEditComment] = useState("");
  const [editSaving, setEditSaving] = useState(false);

  React.useEffect(() => {
    setLocalRequests(alarmRequests || []);
  }, [alarmRequests]);

  const handleUpdateStatus = async (notificationId, status, reason, comment) => {
    setLoading(true);
    try {
      const res = await alertDeviceService.updateStatusAlertDevice({
        notification_id: notificationId,
        status,
        reason,
        comment,
      });
      if (res && res.code === "0000") {
        message.success(res.message || "Status updated");
        setLocalRequests((prev) =>
          prev.map((item) =>
            item.id === notificationId ? { ...item, response_status: status } : item
          )
        );
        if (onStatusUpdated) {
          onStatusUpdated();
        }
      } else {
        message.error(res?.message || "Failed to update status");
      }
    } catch (error) {
      message.error("Failed to update status");
    } finally {
      setLoading(false);
    }
  };

  const handleTerminateClick = (record) => {
    setTerminateRecord(record);
    setTerminateReason("");
    setTerminateComment("");
    setTerminateModalVisible(true);
  };

  const handleTerminateConfirm = async () => {
    if (terminateRecord) {
      await handleUpdateStatus(terminateRecord.id, "terminate", terminateReason, terminateComment);
      setTerminateModalVisible(false);
      setTerminateRecord(null);
    }
  };

  const handleEditClick = (record) => {
    setEditRecord(record);
    setEditReason(record?.reason || "");
    setEditComment(record?.comment || "");
    setEditModalVisible(true);
  };

  const handleEditConfirm = async () => {
    if (!editRecord) return;
    setEditSaving(true);
    try {
      const res = await alertDeviceService.updateAlarmRequest({
        notification_id: editRecord.id,
        reason: editReason,
        comment: editComment,
      });
      if (res && res.code === "0000") {
        message.success(res.message || "Alarm request updated");
        setLocalRequests((prev) =>
          prev.map((item) =>
            item.id === editRecord.id
              ? { ...item, reason: editReason, comment: editComment }
              : item
          )
        );
        if (onStatusUpdated) {
          onStatusUpdated();
        }
        setEditModalVisible(false);
        setEditRecord(null);
      } else {
        message.error(res?.message || "Failed to update alarm request");
      }
    } catch (error) {
      message.error("Failed to update alarm request");
    } finally {
      setEditSaving(false);
    }
  };

  const columnsTable = [
    {
      label: "User",
      accessor: "user",
      minWidth: 100,
      sortable: true,
      position: 1,
      itemAlign: 'left',
      priorityLevel: 1,
      CustomComponent: (cell) => {
        let record = cell.row;
        return <span>{record?.user?.email}</span>;
      },
    },
    {
      label: "Status",
      accessor: "response_status",
      minWidth: 120,
      sortable: true,
      position: 2,
      priorityLevel: 2,
      CustomComponent: (cell) => {
        const record = cell.row;
        const status = record?.response_status;
        if (status) {
          return (
            <Tag color={STATUS_COLORS[status] || "default"}>
              {STATUS_LABELS[status] || status}
            </Tag>
          );
        }
        return <span>-</span>;
      },
    },
    {
      label: "Reason",
      accessor: "reason",
      minWidth: 100,
      sortable: true,
      position: 3,
      priorityLevel: 3,
    },
    {
      label: "Comment",
      accessor: "comment",
      minWidth: 100,
      sortable: true,
      position: 4,
      priorityLevel: 4,
    },
    {
      label: "Created At",
      accessor: "created_at",
      minWidth: 100,
      sortable: true,
      position: 5,
      priorityLevel: 5,
      CustomComponent: (cell) => {
        const record = cell.row;
        return <span>{record?.created_at ? moment.utc(record.created_at).local().format('YYYY-MM-DD HH:mm:ss') : '-'}</span>;
      },
    },
    {
      label: "Actions",
      accessor: "actions",
      minWidth: 200,
      sortable: false,
      position: 6,
      priorityLevel: 1,
      CustomComponent: (cell) => {
        const record = cell.row;
        const isOwnNotification = record?.user_id == currentUserId;
        const status = record?.response_status;

        if (!isOwnNotification) {
          return null;
        }

        if (status === "terminate") {
          return (
            <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
              <Button
                size="small"
                disabled={loading}
                onClick={() => handleEditClick(record)}
              >
                Edit
              </Button>
            </div>
          );
        }

        return (
          <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
            {status !== "not-available" && (
              <Button
                size="small"
                disabled={loading}
                onClick={() => handleUpdateStatus(record.id, "not-available")}
              >
                Not Available
              </Button>
            )}
            {status !== "responding" && (
              <Button
                size="small"
                type="primary"
                disabled={loading}
                onClick={() => handleUpdateStatus(record.id, "responding")}
              >
                Responding
              </Button>
            )}
            <Button
              size="small"
              danger
              disabled={loading}
              onClick={() => handleTerminateClick(record)}
            >
              Stop Alarm
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <StopDeviceAlarmRequestModalWrapper
        width={1000}
        visible={visible}
        onCancel={cancelModal}
        className='modalcust'
        title="Alarm Requests"
        footer={[
          <Button key="back" onClick={cancelModal} className="cancel-button">
            {<IntlMessages id="propertyPage.modal.cancel" />}
          </Button>,
        ]}
      >
        <Table
          rows={localRequests}
          columns={columnsTable}
          size="small"
          pagination={{ pageSize: 12 }}
          rowSize={99999}
          bordered
          className="manage-table"
          rowClassName="rowClassName1"
        />
      </StopDeviceAlarmRequestModalWrapper>

      <Modal
        title="Stop Alarm Confirmation"
        visible={terminateModalVisible}
        onOk={handleTerminateConfirm}
        onCancel={() => setTerminateModalVisible(false)}
        okText="Confirm Stop Alarm"
        okButtonProps={{ danger: true, loading }}
        cancelButtonProps={{ disabled: loading }}
      >
        <div style={{ marginBottom: 12 }}>
          <label>Reason:</label>
          <Input
            value={terminateReason}
            onChange={(e) => setTerminateReason(e.target.value)}
            placeholder="Enter reason"
          />
        </div>
        <div>
          <label>Comment:</label>
          <TextArea
            value={terminateComment}
            onChange={(e) => setTerminateComment(e.target.value)}
            placeholder="Enter comment (optional)"
            rows={3}
          />
        </div>
      </Modal>

      <Modal
        title="Edit Alarm Request"
        visible={editModalVisible}
        onOk={handleEditConfirm}
        onCancel={() => setEditModalVisible(false)}
        okText="Save"
        okButtonProps={{ loading: editSaving }}
        cancelButtonProps={{ disabled: editSaving }}
      >
        <div style={{ marginBottom: 12 }}>
          <label>Reason:</label>
          <Input
            value={editReason}
            onChange={(e) => setEditReason(e.target.value)}
            placeholder="Enter reason"
          />
        </div>
        <div>
          <label>Comment:</label>
          <TextArea
            value={editComment}
            onChange={(e) => setEditComment(e.target.value)}
            placeholder="Enter comment (optional)"
            rows={3}
          />
        </div>
      </Modal>
    </>
  );
};

export default StopDeviceAlarmRequestModal;
